// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { usePageActions } from '../contexts/PageActionsContext';
import { toast } from 'react-toastify';
import { getSessionUserEmail } from '../config/common';
import * as action from '../Action/Preferences/Preferences_Action';
import * as profileAction from '../Action/Profile/Profile_Action';
import * as updateSupportContactAction from '../Action/UpdateSupportContact/UpdateSupportContact_Action';
import * as userDetailsAction from '../Action/User_Details/User_Details_Action';
import Loader from './Loader';
import { ROLE_LABELS, hasRole } from '../utils/roles';

/** @param {{ roles: unknown[] }} props */
const Profile = ({ roles }) => {
    const dispatch = useAppDispatch();
    const profileLoading = useAppSelector((state) => state.ProfileReducer);
    const [data, setData] = useState(profileLoading.payload?.data || { contact: {} });
    const [userDetails, setUserDetails] = useState({});
    const [errors, setErrors] = useState({});
    const [updateSupportContactModel, setUpdateSupportContactModel] = useState(false);
    const [isAddSupportContact, setIsAddSupportContact] = useState(false);
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const formRef = useRef();
    const nameRefFocus = useRef();
    const phoneRefFocus = useRef();
    const jobTitleRefFocus = useRef();
    const hasFetched = useRef();
    const emailRefFocus = useRef();

    const pageTitle = hasRole(roles, 'support') ? 'My admin profile' : 'My profile';
    const { setPageActions } = usePageActions();

    useEffect(() => {
        if (isEditing) {
            setPageActions(
                <>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-3"
                        onClick={() => { setIsEditing(false); setErrors({}); }}
                    >
                        <i className="fa-solid fa-xmark me-1" aria-hidden />
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary rounded-3"
                        onClick={() => formRef.current?.requestSubmit()}
                    >
                        <i className="fa-solid fa-floppy-disk me-1" aria-hidden />
                        Save changes
                    </button>
                </>,
            );
        } else if (data.Status__c === 'Approved') {
            setPageActions(
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-3"
                    onClick={() => setIsEditing(true)}
                >
                    <i className="fa-solid fa-pencil me-1" aria-hidden />
                    Edit
                </button>,
            );
        } else {
            setPageActions(null);
        }
        return () => setPageActions(null);
    }, [isEditing, data.Status__c, setPageActions]);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        getProfileData();
        getUserDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getUserDetails = () => {
        const param = { email: getSessionUserEmail() };
        dispatch(userDetailsAction.user_details({ data: JSON.stringify(param) }))
            .then((response) => {
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setUserDetails(response.data);
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const getProfileData = () => {
        setIsLoading(true);
        const param = { email: getSessionUserEmail() };
        dispatch(profileAction.profile({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setData(response.data);
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
                toast.error(error.message);
            });
    };

    const updatePreferences = (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const formValues = Object.fromEntries(formData.entries());

        formValues.roles = data.roles.filter((item, index) => formRef.current[`role${index}`].checked);
        formValues.notificationPreferences = data.notificationPreferences.filter((item, index) => true);
        formValues.businessSegments = data.businessSegments.filter((item, index) => formRef.current[`seg${index}`].checked);

        setErrors({});
        let customErrors = {};
        if (formValues.name === '' || formValues.name.trim().length === 0) {
            customErrors = { ...customErrors, name: 'Please enter your name to continue.' };
            nameRefFocus.current.focus();
        } else if (formValues.name.trim().split(' ').length < 2) {
            customErrors = { ...customErrors, name: 'Please enter first and last name.' };
            nameRefFocus.current.focus();
        } else if (formValues.phone === '' || formValues.phone.trim().length === 0) {
            customErrors = { ...customErrors, phone: 'Please enter your phone to continue.' };
            phoneRefFocus.current.focus();
        } else if (formValues.jobTitle === '' || formValues.jobTitle.trim().length === 0) {
            customErrors = { ...customErrors, jobTitle: 'Please enter your job title to continue.' };
            jobTitleRefFocus.current.focus();
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors);
            return true;
        }
        if (formValues.roles.length === 0) {
            toast.error('Please select role to continue.');
            return;
        }
        if (!formValues.seniority || formValues.seniority.trim().length === 0) {
            toast.error('Please select seniority to continue.');
            return;
        }
        if (formValues.notificationPreferences.length === 0) {
            toast.error('Please select notification preference to continue.');
            return;
        }

        setIsLoading(true);
        let originUrls = window.location.ancestorOrigins;
        if (originUrls) {
            originUrls = originUrls.length > 0 ? originUrls[originUrls.length - 1] : '';
        }
        const param = {
            role: formValues.roles.join(';'),
            seniority: formValues.seniority,
            notification: formValues.notificationPreferences.join(';'),
            businessSegment: formValues.businessSegments.join(';'),
            url: originUrls
                ? originUrls
                : window.location !== window.parent.location
                  ? document.referrer
                  : document.location.href,
            email: getSessionUserEmail(),
            name: formValues.name,
            phone: formValues.phone,
            jobTitle: formValues.jobTitle,
        };
        dispatch(action.preferences({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setErrors({});
                setIsEditing(false);
                toast.success('Profile updated successfully.');
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
                toast.error(error.message);
            });
    };

    const onInputChange = (e, name) => {
        setData({ ...data, [name]: e.target.value });
    };

    const showAddSupportContact = () => {
        setIsAddSupportContact(true);
        setUpdateSupportContactModel(true);
    };

    const showRemoveSupportContact = () => {
        setIsAddSupportContact(false);
        setUpdateSupportContactModel(true);
    };

    const hideUpdateSupportContact = () => {
        setUpdateSupportContactModel(false);
        setEmail('');
        setErrors({});
    };

    const updateSupportContact = () => {
        setErrors({});
        let customErrors = {};
        if (email === '' || email.trim().length === 0) {
            customErrors = { ...customErrors, email: 'Please enter email address.' };
            emailRefFocus.current.focus();
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors);
            return true;
        }
        setIsLoading(true);
        const param = {
            email: getSessionUserEmail(),
            contactEmail: email,
            isAdd: isAddSupportContact,
        };
        dispatch(updateSupportContactAction.updateSupportContact({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setErrors({});
                toast.success(
                    'Support contact ' + (isAddSupportContact ? 'added' : 'removed') + ' successfully.',
                );
                hideUpdateSupportContact();
                getProfileData();
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
                toast.error(error.message);
            });
    };

    const selectedRoles = data.Role__c
        ? (Array.isArray(data.Role__c) ? data.Role__c : data.Role__c.split(';')).filter(Boolean)
        : [];
    const selectedSegments = data.Business_Segments__c
        ? (Array.isArray(data.Business_Segments__c)
              ? data.Business_Segments__c
              : data.Business_Segments__c.split(';')
          ).filter(Boolean)
        : [];

    const displayEmail =
        getSessionUserEmail() || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('copilotUserEmail')) || '—';

    return (
        <>
            {isLoading && <Loader />}

            <div className="container-fluid pt-1 pb-4 px-3 copilot-changelog copilot-profile-page">
                {!isEditing ? (
                    <div className="copilot-changelog-card">
                        <div className="copilot-changelog-card__accent" aria-hidden />
                        <div className="copilot-changelog-card__body">
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h2 className="copilot-changelog-section-title mb-0">
                                            {data.User_Name__c || '—'}
                                        </h2>
                                        {data.Seniority__c ? (
                                            <span className="badge rounded-pill text-bg-primary" style={{ fontSize: '11px' }}>
                                                {data.Seniority__c}
                                            </span>
                                        ) : null}
                                    </div>
                                    {data.Job_Title__c ? (
                                        <p className="text-body-secondary small mb-0 mt-1">{data.Job_Title__c}</p>
                                    ) : null}
                                </div>
                            </div>

                            <dl className="copilot-profile-meta mb-3">
                                <div>
                                    <dt>Company</dt>
                                    <dd>{data.contact?.companyName || '—'}</dd>
                                </div>
                                <div>
                                    <dt>Phone</dt>
                                    <dd>{data.Contact_Number__c || '—'}</dd>
                                </div>
                                <div>
                                    <dt>Email</dt>
                                    <dd>{displayEmail}</dd>
                                </div>
                            </dl>

                            {selectedRoles.length > 0 ? (
                                <div className="mb-2">
                                    <p className="copilot-changelog-detail-section-title mb-2">Role(s)</p>
                                    <div className="d-flex flex-wrap gap-1">
                                        {selectedRoles.map((r) => (
                                            <span key={r} className="badge rounded-pill bg-secondary">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {selectedSegments.length > 0 ? (
                                <div className="mb-2">
                                    <p className="copilot-changelog-detail-section-title mb-2">Segment(s)</p>
                                    <div className="d-flex flex-wrap gap-1">
                                        {selectedSegments.map((s) => (
                                            <span key={s} className="badge rounded-pill bg-info text-dark">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {roles && roles.length > 0 ? (
                                <div>
                                    <p className="copilot-changelog-detail-section-title mb-2">System roles</p>
                                    <div className="d-flex flex-wrap gap-1">
                                        {roles.map(
                                            (role) =>
                                                ROLE_LABELS[role] && (
                                                    <span
                                                        key={role}
                                                        className="badge rounded-pill bg-dark bg-opacity-25"
                                                        style={{ fontSize: '10px' }}
                                                        title={ROLE_LABELS[role].description}
                                                    >
                                                        {ROLE_LABELS[role].label}
                                                    </span>
                                                ),
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <div className="copilot-changelog-card">
                        <div className="copilot-changelog-card__accent" aria-hidden />
                        <div className="copilot-changelog-card__body">
                            {data.Status__c === 'Requested' ? (
                                <p className="copilot-changelog-section-lead">
                                    You will be able to edit your profile once it has been approved.
                                </p>
                            ) : null}
                            <form ref={formRef} onSubmit={updatePreferences}>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="login-form-label" htmlFor="profile-company">
                                            Company
                                        </label>
                                        <input
                                            id="profile-company"
                                            className="form-control login-form-control rounded-3"
                                            type="text"
                                            name="companyName"
                                            value={data.contact?.companyName}
                                            disabled
                                            maxLength="255"
                                            readOnly
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="login-form-label" htmlFor="profile-name">
                                            Full name
                                        </label>
                                        <input
                                            id="profile-name"
                                            className="form-control login-form-control rounded-3"
                                            ref={nameRefFocus}
                                            value={data.User_Name__c}
                                            onChange={(e) => onInputChange(e, 'User_Name__c')}
                                            disabled={data.Status__c !== 'Approved'}
                                            type="text"
                                            name="name"
                                            maxLength="255"
                                        />
                                        {errors?.name ? (
                                            <div className="invalid-feedback d-block small text-danger">{errors.name}</div>
                                        ) : null}
                                    </div>
                                    <div className="col-12">
                                        <label className="login-form-label" htmlFor="profile-job">
                                            Job title
                                        </label>
                                        <input
                                            id="profile-job"
                                            className="form-control login-form-control rounded-3"
                                            ref={jobTitleRefFocus}
                                            value={data.Job_Title__c}
                                            onChange={(e) => onInputChange(e, 'Job_Title__c')}
                                            disabled={data.Status__c !== 'Approved'}
                                            type="text"
                                            name="jobTitle"
                                            maxLength="255"
                                        />
                                        {errors?.jobTitle ? (
                                            <div className="invalid-feedback d-block small text-danger">{errors.jobTitle}</div>
                                        ) : null}
                                    </div>
                                    <div className="col-12">
                                        <label className="login-form-label" htmlFor="profile-phone">
                                            Contact number
                                        </label>
                                        <input
                                            id="profile-phone"
                                            className="form-control login-form-control rounded-3"
                                            ref={phoneRefFocus}
                                            value={data.Contact_Number__c}
                                            onChange={(e) => onInputChange(e, 'Contact_Number__c')}
                                            disabled={data.Status__c !== 'Approved'}
                                            type="text"
                                            name="phone"
                                            maxLength="255"
                                        />
                                        {errors?.phone ? (
                                            <div className="invalid-feedback d-block small text-danger">{errors.phone}</div>
                                        ) : null}
                                    </div>
                                    <div className="col-md-4">
                                        <p className="login-form-label mb-2">Role</p>
                                        {data.roles &&
                                            data.roles.map((item, index) => (
                                                <div key={index} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name="roles"
                                                        value={item}
                                                        checked={data.Role__c && data.Role__c.indexOf(item) !== -1}
                                                        id={`role${index}`}
                                                        disabled={data.Status__c !== 'Approved'}
                                                        onChange={() => {
                                                            const checkedRoles = data.roles.filter(
                                                                (role, i) => formRef.current[`role${i}`].checked,
                                                            );
                                                            setData({ ...data, Role__c: checkedRoles });
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`role${index}`}>
                                                        {item}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                    <div className="col-md-4">
                                        <p className="login-form-label mb-2">Seniority</p>
                                        {data.seniorities &&
                                            data.seniorities.map((item, index) => (
                                                <div key={index} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="seniority"
                                                        value={item}
                                                        checked={data.Seniority__c && data.Seniority__c === item}
                                                        id={`seniority${index}`}
                                                        disabled={data.Status__c !== 'Approved'}
                                                        onChange={(e) => onInputChange(e, 'Seniority__c')}
                                                    />
                                                    <label className="form-check-label" htmlFor={`seniority${index}`}>
                                                        {item}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                    <div className="col-md-4">
                                        <p className="login-form-label mb-2">Business segments</p>
                                        {data.businessSegments &&
                                            data.businessSegments.map((item, index) => (
                                                <div key={index} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name="businessSegments"
                                                        value={item}
                                                        checked={
                                                            data.Business_Segments__c &&
                                                            data.Business_Segments__c.indexOf(item) !== -1
                                                        }
                                                        id={`seg${index}`}
                                                        disabled={data.Status__c !== 'Approved'}
                                                        onChange={() => {
                                                            const checked = data.businessSegments.filter(
                                                                (role, i) => formRef.current[`seg${i}`].checked,
                                                            );
                                                            setData({ ...data, Business_Segments__c: checked });
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`seg${index}`}>
                                                        {item}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <div className="copilot-changelog-card">
                    <div className="copilot-changelog-card__accent copilot-changelog-card__accent--muted" aria-hidden />
                    <div className="copilot-changelog-card__body">
                        <h2 className="copilot-changelog-section-title">Support contacts</h2>
                        <p className="copilot-changelog-section-lead">
                            People who can help with support requests for your organization.
                        </p>
                        {userDetails.supportContacts && userDetails.supportContacts.length > 0 ? (
                            <ul className="list-unstyled copilot-profile-contact-list mb-3">
                                {userDetails.supportContacts.map((item, index) => (
                                    <li key={index} className="copilot-profile-contact-item">
                                        <span className="fw-semibold">{item.Name}</span>
                                        {item.Email ? (
                                            <span className="text-body-secondary ms-2 small">({item.Email})</span>
                                        ) : null}
                                        {item.Id === userDetails.currentContactId ? (
                                            <span className="badge rounded-pill bg-secondary ms-2">You</span>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-body-secondary small mb-3">No support contacts available.</p>
                        )}
                        {hasRole(roles, 'support') && data.Status__c === 'Approved' ? (
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary login-form-submit btn-sm"
                                    onClick={showAddSupportContact}
                                >
                                    Add contact
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary login-form-secondary btn-sm"
                                    onClick={showRemoveSupportContact}
                                >
                                    Remove contact
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {updateSupportContactModel && (
                <>
                    <div
                        className="modal fade show copilot-changelog-upgrade-modal-root"
                        id="updateSupportContact"
                        role="dialog"
                        aria-labelledby="updateSupportContactLabel"
                        aria-modal="true"
                        aria-hidden="false"
                        style={{ display: 'block' }}
                    >
                        <div className="modal-dialog modal-dialog-centered mw-570 px-2" role="document">
                            <div className="modal-content copilot-changelog-upgrade-modal">
                                <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                                <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                                    <div className="flex-grow-1 min-w-0 pe-2">
                                        <h2
                                            className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title"
                                            id="updateSupportContactLabel"
                                        >
                                            {isAddSupportContact
                                                ? 'Add support contact'
                                                : 'Remove support contact'}
                                        </h2>
                                        <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                            {isAddSupportContact
                                                ? 'Enter the work email of someone who should receive support requests for your organization.'
                                                : 'Enter the email of the support contact to remove from your organization.'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                        aria-label="Close"
                                        onClick={hideUpdateSupportContact}
                                    />
                                </div>
                                <form
                                    id="profile-support-contact-form"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        updateSupportContact();
                                    }}
                                >
                                    <div className="modal-body copilot-changelog-upgrade-modal__body">
                                        <div className="copilot-changelog-upgrade-modal__fields">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="support-contact-email"
                                            >
                                                Email
                                            </label>
                                            <input
                                                id="support-contact-email"
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control rounded-3"
                                                ref={emailRefFocus}
                                                type="email"
                                                name="contactEmail"
                                                maxLength="255"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                autoComplete="email"
                                                placeholder="name@company.com"
                                            />
                                            {errors?.email ? (
                                                <div className="invalid-feedback d-block small text-danger mt-1">
                                                    {errors.email}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary login-form-secondary"
                                            onClick={hideUpdateSupportContact}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-sm btn-primary login-form-submit"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
                </>
            )}
        </>
    );
};

export default Profile;
